import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useSavedSearches, useCreateSavedSearch } from "@/hooks/use-api";

interface SavedSearchesProps {
  currentQuery: Record<string, unknown> | null;
  onLoadSearch: (queryJson: Record<string, unknown>) => void;
}

export function SavedSearches({
  currentQuery,
  onLoadSearch,
}: SavedSearchesProps) {
  const [saveName, setSaveName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);

  const { data } = useSavedSearches();
  const saveMutation = useCreateSavedSearch();

  const savedSearches = data?.saved_searches ?? [];

  async function handleSave() {
    if (!saveName.trim() || !currentQuery) return;
    await saveMutation.mutateAsync({
      name: saveName.trim(),
      query_json: currentQuery,
    });
    setSaveName("");
    setShowSaveInput(false);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {savedSearches.length > 0 && (
        <>
          <span className="text-xs text-muted-foreground">Saved:</span>
          {savedSearches.slice(0, 5).map((search) => (
            <Badge
              key={search.id}
              variant="outline"
              className="cursor-pointer hover:bg-accent"
              onClick={() => onLoadSearch(search.query_json)}
            >
              {search.name}
            </Badge>
          ))}
        </>
      )}

      {currentQuery && (
        <>
          {showSaveInput ? (
            <div className="flex items-center gap-1">
              <Input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="Search name"
                className="h-7 w-32 text-xs"
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") setShowSaveInput(false);
                }}
              />
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                onClick={handleSave}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => setShowSaveInput(true)}
            >
              <Star className="mr-1 h-3 w-3" />
              Save
            </Button>
          )}
        </>
      )}
    </div>
  );
}
